import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { DatePickerCell } from '@/components/ui/date-picker';
import api from '@/lib/api';
import { toast } from 'sonner';
import MacroLinkDropdown from '@/components/rollup/MacroLinkDropdown';

const STATUS_STYLES = {
  COMPLETED: { bg: 'var(--status-completed-bg)', color: 'var(--status-completed-text)', label: 'COMPLETED' },
  ON_TRACK: { bg: 'var(--status-on-track-bg)', color: 'var(--status-on-track-text)', label: 'ON TRACK' },
  AHEAD: { bg: 'var(--status-ahead-bg)', color: 'var(--status-ahead-text)', label: 'AHEAD' },
  DELAYED: { bg: 'var(--status-delayed-bg)', color: 'var(--status-delayed-text)', label: 'DELAYED' },
  OVERDUE: { bg: 'var(--status-delayed-bg)', color: 'var(--status-delayed-text)', label: 'OVERDUE' },
  IN_PROGRESS: { bg: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b', label: 'IN PROGRESS' },
  NOT_STARTED: { bg: 'var(--badge-muted-bg)', color: 'var(--text-disabled)', label: 'NOT STARTED' },
};

const GRID_COLS = '36px 150px 1fr 64px 100px 120px 100px 140px 28px';

function StatusBadge({ status }) {
  const style = STATUS_STYLES[status] || STATUS_STYLES.NOT_STARTED;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold tracking-wide whitespace-nowrap"
      style={{ backgroundColor: style.bg, color: style.color }}>
      {style.label}
    </span>
  );
}

function TypeBadge({ code }) {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold"
      style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)', border: '1px solid rgba(56, 96, 255, 0.15)' }}>
      {code}
    </span>
  );
}

function ProgressBar({ value = 0, status }) {
  const style = STATUS_STYLES[status] || STATUS_STYLES.ON_TRACK;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--border-color)' }}>
        <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(value, 100)}%`, backgroundColor: style.color }} />
      </div>
      <span className="text-xs font-medium min-w-[32px] text-right" style={{ color: style.color }}>
        {value.toFixed(0)}%
      </span>
    </div>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
}

function EntryRow({ entry, index, projectId, onRefresh }) {
  const [expanded, setExpanded] = useState(false);

  const handleDateChange = async (stageGateId, dateField, newValue) => {
    try {
      await api.put(`/v1/projects/${projectId}/engineering-schedule/entries/${entry.id}/stage-gates/${stageGateId}`, {
        [dateField]: newValue
      });
      toast.success(`Date updated`);
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update');
    }
  };

  return (
    <div className="rounded-lg overflow-hidden" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}
      data-testid={`register-row-${entry.id}`}>
      {/* Main Row */}
      <div className="grid items-center gap-3 px-4 py-3 cursor-pointer transition-colors hover:opacity-90"
        style={{ gridTemplateColumns: GRID_COLS }}
        onClick={() => setExpanded(!expanded)}>
        <span className="text-xs font-mono text-center" style={{ color: 'var(--text-disabled)' }}>{index + 1}</span>
        <div className="min-w-0">
          <span className="text-sm font-medium truncate block" style={{ color: 'var(--accent-text)' }}>{entry.document_number}</span>
        </div>
        <div className="min-w-0">
          <span className="text-sm truncate block" style={{ color: 'var(--text-primary)' }}>{entry.document_title}</span>
        </div>
        <div className="text-center"><TypeBadge code={entry.document_type_code} /></div>
        <span className="text-sm text-center truncate" style={{ color: 'var(--text-secondary)' }}>{entry.discipline}</span>
        <ProgressBar value={entry.overall_progress || 0} status={entry.overall_status} />
        <div className="text-center"><StatusBadge status={entry.overall_status} /></div>
        <div className="flex items-center justify-center">
          <MacroLinkDropdown
            projectId={projectId}
            microModule="DCI"
            microActivityId={entry.id}
            microActivityCode={entry.document_number}
            microActivityName={entry.document_title}
          />
        </div>
        <div className="flex items-center justify-center" style={{ color: 'var(--text-disabled)' }}>
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </div>
      </div>

      {/* Expanded Stage Gate Details */}
      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden" style={{ borderTop: '1px solid var(--border-color)' }}>
            <div className="px-6 py-4 space-y-3" style={{ backgroundColor: 'var(--card-bg-alt)' }}>
              <h4 className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                Stage Gate Progress
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid={`stage-gates-table-${entry.id}`}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                      {['Stage Gate', 'Weight', 'Planned', 'Forecast', 'Actual', 'Status', 'Rev'].map((h) => (
                        <th key={h} className="text-left py-2 px-3 text-xs font-medium" style={{ color: 'var(--text-muted)' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(entry.stage_gate_progress || []).map((sg, idx) => (
                      <tr key={sg.stage_gate_id} style={{ borderBottom: idx < entry.stage_gate_progress.length - 1 ? '1px solid var(--border-color)' : 'none' }}>
                        <td className="py-2.5 px-3 font-medium" style={{ color: 'var(--text-primary)' }}>{sg.stage_gate_name}</td>
                        <td className="py-2.5 px-3" style={{ color: 'var(--text-secondary)' }}>{sg.weightage_percent}%</td>
                        <td className="py-2.5 px-3">
                          <DatePickerCell value={sg.planned_date} dateColor="var(--text-secondary)"
                            onChange={(v) => handleDateChange(sg.stage_gate_id, 'planned_date', v)} />
                        </td>
                        <td className="py-2.5 px-3">
                          <DatePickerCell value={sg.forecast_date} dateColor="#F59E0B"
                            onChange={(v) => handleDateChange(sg.stage_gate_id, 'forecast_date', v)} />
                        </td>
                        <td className="py-2.5 px-3">
                          <DatePickerCell value={sg.actual_date} dateColor="var(--status-completed-text)"
                            onChange={(v) => handleDateChange(sg.stage_gate_id, 'actual_date', v)} />
                        </td>
                        <td className="py-2.5 px-3"><StatusBadge status={sg.status} /></td>
                        <td className="py-2.5 px-3" style={{ color: 'var(--text-disabled)' }}>{sg.revision_number || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {(entry.area || entry.contractor) && (
                <p className="text-xs pt-1" style={{ color: 'var(--text-disabled)' }}>
                  {entry.area && `Area: ${entry.area}`}
                  {entry.area && entry.contractor && ' \u2022 '}
                  {entry.contractor && `Contractor: ${entry.contractor}`}
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function DCIRegisterList({ entries, projectId, onRefresh }) {
  return (
    <div className="space-y-2" data-testid="dci-register-list">
      {/* Table Header */}
      <div className="grid items-center gap-3 px-4 py-2" style={{ gridTemplateColumns: GRID_COLS }}>
        <span className="text-xs font-medium text-center" style={{ color: 'var(--text-muted)' }}>#</span>
        <span className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Doc Number</span>
        <span className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Title</span>
        <span className="text-xs font-medium text-center" style={{ color: 'var(--text-muted)' }}>Type</span>
        <span className="text-xs font-medium text-center" style={{ color: 'var(--text-muted)' }}>Discipline</span>
        <span className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Progress</span>
        <span className="text-xs font-medium text-center" style={{ color: 'var(--text-muted)' }}>Status</span>
        <span className="text-xs font-medium text-center" style={{ color: 'var(--text-muted)' }}>Macro Link</span>
        <span className="w-4" />
      </div>

      {/* Entries */}
      {entries.map((entry, idx) => (
        <EntryRow key={entry.id} entry={entry} index={idx} projectId={projectId} onRefresh={onRefresh} />
      ))}

      {entries.length === 0 && (
        <div className="text-center py-16" style={{ color: 'var(--text-muted)' }}>
          <p className="text-lg font-medium">No documents found</p>
          <p className="text-sm mt-1" style={{ color: 'var(--text-disabled)' }}>Add entries or adjust your filters</p>
        </div>
      )}
    </div>
  );
}
